/**
 * Browser AHP relay connection runtime.
 *
 * Owns one `AhpClient` + Web PubSub transport pair for a single Mission Control
 * mediated connection to an environment. It connects, discovers the session to
 * observe, applies the subscription snapshot once, then fans every live action
 * out to the caller. Transport drops trigger a token-refreshed re-establish;
 * the snapshot re-applied on reconnect recovers any missed actions (the
 * consumer's projector is idempotent).
 *
 * Dispatches user turns and tool-call confirmation responses for an observed session.
 */

import {AhpClient, RpcError, type AhpTransport, type Subscription} from '@microsoft/agent-host-protocol/client'
import type {
  ActionEnvelope,
  ActionType,
  ChatState,
  MessageAttachment,
  MessageKind,
  ModelSelection,
  PendingMessageKind,
  ReconnectResult,
  RootAction,
  RootState,
  SessionAction,
  SessionActiveClient,
  SessionState,
  SessionToolAuthenticationRequest,
  StateAction,
  ToolCallResult,
  ToolDefinition,
  URI,
} from '@microsoft/agent-host-protocol'
import {AhpErrorCodes, rootReducer, sessionReducer} from '@microsoft/agent-host-protocol'
import {WpsTransport, type WebSocketFactory} from './webpubsub/wps-transport'
import {parseGroupName} from './webpubsub/groups'
import {
  buildWpsUrl,
  connectToEnvironment,
  reconnectToEnvironment,
  type MCConnectResponse,
  type MCFetchOptions,
  type MCFetcher,
} from './mission-control/mission-control-environment-client'
import {
  createAwpsAuthenticationContext,
  AUTH_REQUIRED_CODE,
  authRequiredResources,
  defaultResourceTokenProvider,
  satisfyResources,
  withFallbackResourceTokenProvider,
  type AwpsAuthenticationContext,
  type ResourceTokenProvider,
} from './protected-resource-auth'
import {
  createSessionWithAuth,
  sealedGitHubTokenProvider,
  toWorkingDirectoryUri,
  type CreateSessionParams,
} from './session-host/create-session'
import {
  ProjectProvisioningError,
  resolveProjectWorkingDirectory,
  type ProvisioningStatus,
  type ResolvedProject,
} from './session-host/resolve-project'
import {asAhpVersionMismatch, CLIENT_SUPPORTED_AHP_VERSIONS} from './protocol-support'
import {buildInputCompletedAction, type BuildInputCompletedActionParams} from './ahp-input-actions'
import {isPassiveHandshakeResult} from './passive-connection'
import {createClientTurnId} from './turn-identity'

const ROOT_CHANNEL = 'ahp-root://' as const
/** Fallback clone-auth resource, used only when the daemon advertises no protected resources of its own. */
const GITHUB_API_RESOURCE = 'https://api.github.com'
const SESSION_CONFIG_TARGET_WORKTREE = 'worktree'
const ACTION_TURN_STARTED = 'chat/turnStarted' as ActionType.ChatTurnStarted
const ACTION_ACTIVE_CLIENT_SET = 'session/activeClientSet' as ActionType.SessionActiveClientSet
const ACTION_TOOL_CALL_START = 'chat/toolCallStart' as ActionType.ChatToolCallStart
const ACTION_TOOL_CALL_DELTA = 'chat/toolCallDelta' as ActionType.ChatToolCallDelta
const ACTION_TOOL_CALL_READY = 'chat/toolCallReady' as ActionType.ChatToolCallReady
const ACTION_TOOL_CALL_CONFIRMED = 'chat/toolCallConfirmed' as ActionType.ChatToolCallConfirmed
const ACTION_TOOL_CALL_COMPLETE = 'chat/toolCallComplete' as ActionType.ChatToolCallComplete
const ACTION_TURN_CANCELLED = 'chat/turnCancelled' as ActionType.ChatTurnCancelled
const ACTION_TURN_COMPLETE = 'chat/turnComplete' as ActionType.ChatTurnComplete
const ACTION_TURN_ERROR = 'chat/error' as ActionType.ChatError
const ACTION_SESSION_META_CHANGED = 'session/metaChanged' as ActionType.SessionMetaChanged
const SESSION_INPUT_KIND_TOOL_AUTHENTICATION = 'toolAuthentication'
/**
 * Session `_meta` key carrying the latest failed model call. copilotd reports
 * some failures (e.g. `model_not_supported`) only here while still marking the
 * turn `complete`, so without reading it the failed turn renders as blank.
 */
const META_KEY_MODEL_CALL_FAILURE = 'copilot.modelCallFailure'
const ACTION_PENDING_MESSAGE_SET = 'chat/pendingMessageSet' as ActionType.ChatPendingMessageSet
const ACTION_PENDING_MESSAGE_REMOVED = 'chat/pendingMessageRemoved' as ActionType.ChatPendingMessageRemoved
const ACTION_SESSION_ACTIVE_CLIENT_SET = 'session/activeClientSet' as ActionType.SessionActiveClientSet
const ACTION_SESSION_TITLE_CHANGED = 'session/titleChanged' as ActionType.SessionTitleChanged
const MESSAGE_KIND_USER = 'user' as MessageKind.User
const PENDING_MESSAGE_KIND_QUEUED = 'queued' as PendingMessageKind.Queued
const CHAT_URI_SUFFIX = '/chat' as const
const RECONNECT_BASE_DELAY_MS = 1_000
const RECONNECT_MAX_DELAY_MS = 30_000
const MAX_AUTH_RETRIES = 1
// JSON-RPC code the daemon returns when a subscribed session does not exist.
const SESSION_NOT_FOUND_CODE = -32008

// An `RpcError` is a deliberate host rejection of a well-formed request, so
// re-sending it on a fresh connection usually cannot change the outcome — it is
// held as a terminal `error` rather than driving the reconnect loop. `-32011`
// (`Conflict`) is the exception: it is an optimistic-concurrency failure against
// stale advertised state (e.g. authenticating a resource the daemon no longer
// advertises, see the note at `establish`), and the spec's prescribed recovery
// is to re-read and retry — which a reconnect does by re-fetching fresh
// snapshots. Add a code here only when the host can genuinely recover from it
// across a reconnect.
const RETRYABLE_RPC_ERROR_CODES: ReadonlySet<number> = new Set([AhpErrorCodes.Conflict])

/** The AHP channel for a session, observed over the relay. */
export function sessionChannel(sessionId: string): URI {
  return `ahp-session:/${sessionId}`
}

/**
 * The per-session chat channel. As of AHP 0.4.0 the conversation lives on a
 * chat, and `copilotd` names the session's default chat by appending `/chat` to
 * the session URI. We prefer the session's advertised `defaultChat` when present
 * and fall back to this deterministic form.
 */
export function chatChannelFor(sessionChannelUri: URI): URI {
  return `${sessionChannelUri}${CHAT_URI_SUFFIX}`
}

export type RelayStatus = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'closed' | 'error'

export interface RelayLogger {
  info(message: string): void
  error(message: string): void
}

const noopLogger: RelayLogger = {info: () => {}, error: () => {}}

export type AhpClientFactory = (transport: AhpTransport) => AhpClient

const defaultAhpClientFactory: AhpClientFactory = transport => new AhpClient(transport)

// Options for the client-driven `createSession` the relay issues on first
// establish, for sessions MC bound but never created on the daemon.
export interface RelayCreateSessionOptions {
  /** Working directory for the session, required when the daemon has no workspace roots. */
  workingDirectory?: string
  /** Model the new session is created with. Omit to use the daemon default. */
  model?: ModelSelection
  /**
   * Resolve this repository to a host project (catalogue match or clone) and bind
   * the session to a git worktree of it; takes precedence over `workingDirectory`.
   */
  project?: RelayProjectProvisioning
  /**
   * Active-client customization published at session creation time. Merged
   * with the tool definitions contributed via
   * {@link RelayConnectionRuntimeOptions.clientTools} into a single
   * `SessionActiveClient` (`clientId` is set by the runtime); the merged
   * publication is re-asserted on every reconnect.
   */
  activeClient?: (clientId: string) => SessionActiveClient
  /**
   * Client-only: subscribe (attach) first and create the daemon session only on
   * `-32008`. For a hard-navigated observe (managed sandbox) whose session may
   * already exist. Not sent to the daemon.
   */
  attachOrCreate?: boolean
}

/** Repository-to-project provisioning request for a worktree-backed session. */
export interface RelayProjectProvisioning {
  nwo: string
  cloneUrl: string
  /**
   * Base branch for the worktree; the host auto-generates the actual working
   * branch. Falls back to the repo's default branch, then the project's main
   * checkout.
   */
  baseBranch?: string
}

/** A tool call the host has routed to this client for execution. */
export interface ClientToolCall {
  toolCallId: string
  turnId: string
  toolName: string
  /** Raw tool input the host streamed for this call (a JSON string), if any. */
  toolInput?: string
}

/**
 * Internal bookkeeping for a routed client tool call between `toolCallStart`
 * and `toolCallReady`. `deltas` accumulates the `chat/toolCallDelta` chunks the
 * host streams for the arguments, so we can fall back to them when
 * `toolCallReady` omits the consolidated `toolInput`.
 */
interface OwnedToolCall {
  toolCallId: string
  turnId: string
  toolName: string
  deltas: string
  /** Set once we've approved a call the host left in `pending-confirmation`. */
  confirmed?: boolean
  /** `toolInput` seen on the confirmation ready, used if a later ready omits it. */
  readyInput?: string
}

/**
 * A tool this client contributes to the observed session. The runtime includes
 * the {@link definition} in the `SessionActiveClient` published via
 * `session/activeClientSet` and — when the host routes a matching call to this
 * client — runs {@link fulfil} and reports the result via
 * `chat/toolCallComplete`.
 *
 * Tools can be registered or unregistered dynamically at any time via
 * {@link RelayConnectionRuntime.registerTool} and
 * {@link RelayConnectionRuntime.unregisterTool}. Each mutation re-publishes
 * `session/activeClientSet` so the host is always aware of the current tool set.
 */
export interface ClientTool {
  definition: ToolDefinition
  /**
   * Execute the tool and return its result. Omit for render-only tools (the
   * timeline renders from the call's `toolInput`); the runtime then completes
   * them with a bare success so the turn can proceed.
   */
  fulfil?: (call: ClientToolCall) => ToolCallResult | Promise<ToolCallResult>
}

export interface RelayConnectionRuntimeOptions {
  environmentId: string
  /** Mission Control session id; the relay observes this session's channel. */
  sessionId: string
  /** Channel URI to observe. Defaults to `ahp-session:/<sessionId>`. */
  channel?: URI
  /** Receives the chat snapshot each time it is (re)established. */
  onSnapshot: (state: ChatState) => void
  /** Receives every live action in `serverSeq` order. */
  onAction: (env: ActionEnvelope) => void
  onStatus?: (status: RelayStatus, error?: string) => void
  /** Receives passivity changes for this AHP connection. Passive means read-only. */
  onPassiveChange?: (passive: boolean) => void
  onProvisioning?: (status: ProvisioningStatus) => void
  /** Receives a `_meta` model-call failure (copilotd reports it without a `chat/error`), tagged with its turn id. */
  onModelCallFailure?: (failure: RelayModelCallFailure, turnId: string) => void
  /** Performs Mission Control requests. Required unless both `mcConnect` and `mcReconnect` are supplied. */
  fetcher?: MCFetcher
  /** Mints WPS credentials. Defaults to the Mission Control connect endpoint. */
  mcConnect?: (options?: MCFetchOptions) => Promise<MCConnectResponse>
  /** Refreshes WPS credentials for reconnect. Defaults to the reconnect endpoint. */
  mcReconnect?: (clientId: string, options?: MCFetchOptions) => Promise<MCConnectResponse>
  // When set, issue `createSession` once on first establish before subscribing.
  createSession?: RelayCreateSessionOptions
  webSocketFactory?: WebSocketFactory
  ahpClientFactory?: AhpClientFactory
  /** Resolves per-resource tokens when the host requires authentication. */
  resourceTokenProvider?: ResourceTokenProvider
  /** Initial client tools to register. Can be extended later via registerTool/unregisterTool. */
  clientTools?: ClientTool[]
  logger?: RelayLogger
}

function isChatState(state: unknown): state is ChatState {
  return !!state && typeof state === 'object' && Array.isArray((state as {turns?: unknown}).turns)
}

function isRootState(state: unknown): state is RootState {
  return !!state && typeof state === 'object' && Array.isArray((state as {agents?: unknown}).agents)
}

function isSessionState(state: unknown): state is SessionState {
  return !!state && typeof state === 'object' && 'defaultChat' in state
}

/** A model-call failure lifted from the session `_meta` side-channel, normalized for any consumer. */
export interface RelayModelCallFailure {
  model?: string
  message: string
  code?: string
  statusCode?: number
}

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined
}

/**
 * copilotd's `errorMessage` is often a JSON string like `{"message":"…"}`; lift
 * the nested message, else fall back to the raw string / a generic message.
 */
function readModelFailureMessage(raw: Record<string, unknown>): string {
  const errorMessage = nonEmptyString(raw.errorMessage)
  if (!errorMessage) return 'The model failed to respond.'
  try {
    const parsed = JSON.parse(errorMessage) as {message?: unknown}
    const nested = nonEmptyString(parsed.message)
    if (nested) return nested
  } catch {
    // Not JSON — use the raw string.
  }
  return errorMessage
}

/** Normalize the `copilot.modelCallFailure` `_meta` payload; `undefined` when the key is absent or malformed. */
export function normalizeModelCallFailure(
  meta: Record<string, unknown> | undefined,
): RelayModelCallFailure | undefined {
  const raw = meta?.[META_KEY_MODEL_CALL_FAILURE]
  if (!raw || typeof raw !== 'object') return undefined
  const record = raw as Record<string, unknown>
  const model = nonEmptyString(record.model)
  const code = nonEmptyString(record.errorCode)
  const statusCode = typeof record.statusCode === 'number' ? record.statusCode : undefined
  return {
    message: readModelFailureMessage(record),
    ...(model ? {model} : {}),
    ...(code ? {code} : {}),
    ...(statusCode !== undefined ? {statusCode} : {}),
  }
}

/** Read the routing-hint chat URI a session points new input at. */
export function defaultChatChannel(state: unknown): URI | undefined {
  if (!state || typeof state !== 'object') return undefined
  const chat = (state as {defaultChat?: unknown}).defaultChat
  return typeof chat === 'string' ? chat : undefined
}

export class RelayConnectionRuntime {
  private readonly logger: RelayLogger
  private readonly ahpClientFactory: AhpClientFactory

  private client: AhpClient | null = null
  private transport: AhpTransport | null = null
  private mcClientId: string | undefined
  private lastSeenServerSeq = 0
  private rootState: RootState | undefined
  private sessionState: SessionState | undefined
  private resourceTokenProvider: ResourceTokenProvider | undefined
  private authentication: AwpsAuthenticationContext | undefined
  private readonly toolAuthenticationInFlight = new Set<string>()
  /** Chat channel resolved from the session's `defaultChat`; the conversation
   * (turns, tool calls) is observed and dispatched here as of AHP 0.4.0. */
  private chatChannel: URI | undefined

  /** Id of the streaming turn, tracked so {@link cancelTurn} can target it. */
  private activeTurnId: string | undefined

  /**
   * Id of the most recently started turn. Unlike {@link activeTurnId} it is NOT
   * cleared when the turn ends, so a model-call failure arriving later on the
   * session `_meta` side-channel can still be attributed to its turn.
   */
  private lastTurnId: string | undefined

  /**
   * `apiCallId`s of already-surfaced model-call failures. The host never clears
   * the latest failure from `_meta`, so it rides along on every later
   * `session/metaChanged`; dedupe so each is surfaced once.
   */
  private readonly seenModelFailureIds = new Set<string>()

  /** Bumped on every connect attempt / teardown to invalidate stale async work. */
  private connectGen = 0
  private connectAbortController: AbortController | undefined
  /** Pending exponential-backoff reconnect timer, cleared on teardown/dispose so a released relay stops reconnecting. */
  private reconnectTimer: ReturnType<typeof setTimeout> | undefined
  private disposed = false
  private reconnectAttempts = 0
  private status: RelayStatus = 'idle'
  /**
   * True while an establish is in flight. A refresh arriving meanwhile is
   * skipped rather than spawning a redundant `/connect`, which collapses a burst
   * of consumers mounting on the same task into a single connection.
   */
  private establishInFlight = false
  /**
   * `connectGen` value that already has a reconnect scheduled. Guards against
   * both the chat and session-keepalive subscriptions racing to schedule a
   * reconnect for the same failed connection: they share a transport, so a
   * network drop terminates both, and without this dedup we would
   * double-increment `reconnectAttempts` and mis-set the exponential backoff.
   */
  private reconnectScheduledForGen: number | undefined
  /**
   * Whether the current `mcClientId` ever reached a healthy `connected` state.
   *
   * Mission Control delivers the daemon spawn (which makes the daemon join this
   * client's `to-host` group and answer `initialize`) ONLY on `/connect`, not on
   * `/reconnect`. So a client whose spawn was dropped or that connected before the
   * daemon's data plane was ready never gets answered, and reconnecting with the
   * same client id loops on the `initialize` timeout forever. We only take the
   * cheap `/reconnect` path once a connection has actually become healthy;
   * otherwise (and after any failed (re)establish) we escalate to a fresh
   * `/connect` so a new client id + new spawn is published.
   */
  private clientEverConnected = false

  // Guards `createSession` to the first establish; reconnects skip it.
  private sessionCreated = false

  /**
   * Tool call ids the host has attributed to this client (from `toolCallStart`
   * with a matching client contributor) and that we still owe a
   * `chat/toolCallComplete` for. Cleared on teardown so a reconnect starts
   * clean.
   */
  private readonly ownedToolCalls = new Map<string, OwnedToolCall>()

  /**
   * `chat/toolCallReady` inputs that arrived before their matching
   * `chat/toolCallStart`. copilotd can publish the two out of order (they travel
   * on separate async paths — see {@link AhpChatStore}'s equivalent buffer), and
   * ownership is only known once `toolCallStart` names our client contributor.
   * Buffered by tool-call id and replayed when an owned start registers the
   * call, so an out-of-order ready never leaves the call (and its turn) stalled
   * waiting for a `chat/toolCallComplete` we would otherwise never send. Cleared
   * on teardown so a reconnect starts clean.
   */
  private readonly pendingClientToolReady = new Map<string, {toolInput?: string; needsConfirmation: boolean}>()

  /**
   * Set once a connection attempt fails with a permanent, non-retryable
   * condition (a deliberate host `RpcError`, a protocol-version mismatch, or a
   * terminal provisioning failure). The runtime then holds a terminal `error`
   * status and stops the reconnect loop until it is disposed.
   */
  private terminated = false

  /** Mutable list of client tools. Mutated via registerTool/unregisterTool. */
  private readonly tools: ClientTool[] = []

  private readonly options: RelayConnectionRuntimeOptions
  private passive = false
  private passiveKnown = false

  constructor(options: RelayConnectionRuntimeOptions) {
    this.options = options
    this.logger = options.logger ?? noopLogger
    this.ahpClientFactory = options.ahpClientFactory ?? defaultAhpClientFactory
    if (options.clientTools) {
      this.tools.push(...options.clientTools)
    }
  }

  private observedChannel(): URI {
    return this.options.channel ?? sessionChannel(this.options.sessionId)
  }

  /**
   * Handle a failed (re)establish. Returns `true` when the failure is terminal
   * — surfaced as an `error` status with no reconnect — so callers skip their
   * retry path. Terminal conditions are a protocol-version mismatch, a terminal
   * provisioning failure, or any deliberate host `RpcError` outside the
   * retryable allowlist.
   */
  private async handleTerminalEstablishError(err: unknown): Promise<boolean> {
    const message =
      this.detectVersionMismatch(err) ?? this.detectProvisioningFailure(err) ?? this.detectTerminalRpcError(err)
    if (message === null) return false
    // A partially-established connection may have already assigned an open
    // transport/client; release it before settling into the terminal `error`
    // state, since no later reconnect will run to clean it up.
    await this.teardownClient()
    this.setStatus('error', message)
    return true
  }

  private detectVersionMismatch(err: unknown): string | null {
    const mismatch = asAhpVersionMismatch(err)
    if (!mismatch) return null
    this.terminated = true
    this.clientEverConnected = false
    this.logger.error(`relay connect failed: ${mismatch.message}`)
    return mismatch.message
  }

  private detectProvisioningFailure(err: unknown): string | null {
    if (!(err instanceof ProjectProvisioningError)) return null
    // A `closed` reason means the root subscription dropped mid-provision, not a
    // genuine clone failure — let the normal reconnect re-drive provisioning.
    if (err.reason === 'closed') return null
    this.terminated = true
    this.clientEverConnected = false
    this.logger.error(`relay project provisioning failed: ${err.message}`)
    // Some failure paths (e.g. unsupported host) throw before emitting progress,
    // so surface the terminal status here rather than relying on the resolve step.
    this.options.onProvisioning?.({phase: 'failed', error: err.message})
    return err.message
  }

  /**
   * A deliberate host rejection of a well-formed request. Retrying the identical
   * request on a fresh connection cannot change the outcome, so it is held as a
   * terminal `error` unless its code is in the retryable allowlist.
   */
  private detectTerminalRpcError(err: unknown): string | null {
    if (!(err instanceof RpcError) || RETRYABLE_RPC_ERROR_CODES.has(err.code)) return null
    this.terminated = true
    this.clientEverConnected = false
    const msg = formatError(err)
    this.logger.error(`relay connect failed (terminal): ${msg}`)
    return msg
  }

  async connect(): Promise<void> {
    if (this.disposed) throw new Error('relay connection runtime is disposed')
    const gen = ++this.connectGen
    this.establishInFlight = true
    const signal = this.beginConnectAttempt()
    this.setStatus('connecting')
    try {
      await this.establish(gen, false, signal)
    } catch (err) {
      if (gen !== this.connectGen || this.disposed) return
      if (await this.handleTerminalEstablishError(err)) return
      // The (re)establish failed before becoming healthy; force the next
      // attempt to take the full `/connect` path (new client id + spawn).
      this.clientEverConnected = false
      const msg = formatError(err)
      this.logger.error(`relay connect failed: ${msg}`)
      this.setStatus('error', msg)
      this.scheduleReconnect(gen)
    } finally {
      this.settleEstablish(gen)
      this.finishConnectAttempt(signal)
    }
  }

  /** Clear the in-flight guard, unless a newer attempt has superseded this one. */
  private settleEstablish(gen: number): void {
    if (gen !== this.connectGen) return
    this.establishInFlight = false
  }

  private dispatchOnChat(action: StateAction): void {
    if (this.passive) throw new Error('relay connection is read-only')
    const client = this.client
    if (!client) throw new Error('relay is not connected')
    const chat = this.chatChannel
    if (!chat) throw new Error('relay chat channel is not resolved')
    client.dispatch(chat, action)
  }

  private dispatchOnSession(action: StateAction): void {
    if (this.passive) throw new Error('relay connection is read-only')
    const client = this.client
    if (!client) throw new Error('relay is not connected')
    client.dispatch(this.observedChannel(), action)
  }

  /** Publish the tools and customizations this web client contributes to the observed session. */
  publishActiveClient(activeClient: SessionActiveClient): void {
    this.dispatchOnSession({
      type: ACTION_SESSION_ACTIVE_CLIENT_SET,
      activeClient,
    })
  }

  /** Complete a running client-provided tool call on the observed chat channel. */
  completeToolCall({turnId, toolCallId, result}: {turnId: string; toolCallId: string; result: ToolCallResult}): void {
    this.dispatchOnChat({
      type: ACTION_TOOL_CALL_COMPLETE,
      turnId,
      toolCallId,
      result,
    })
  }

  /** The Mission Control/AHP client id for the current live connection. */
  getClientId(): string {
    if (!this.mcClientId) throw new Error('relay client id is not resolved')
    return this.mcClientId
  }

  /** Rename the observed AHP session. Requires a live connection. */
  renameSession(title: string): void {
    this.dispatchOnSession({
      type: ACTION_SESSION_TITLE_CHANGED,
      title,
    })
  }

  /** Toggle per-session auto-approval ("allow all") on the host via the
   * `extensions/setSessionApproveAll` vendor RPC. Awaited so failures surface. */
  async setSessionApproveAll(enabled: boolean): Promise<void> {
    if (this.passive) throw new Error('relay connection is read-only')
    const client = this.client
    if (!client) throw new Error('relay is not connected')
    // Vendor `extensions/*` method the typed CommandMap doesn't cover; one contained cast.
    const send = client.request as unknown as (method: string, params: unknown) => Promise<unknown>
    await send.call(client, 'extensions/setSessionApproveAll', {channel: this.observedChannel(), enabled})
  }

  /** Dispatch a new user turn on the observed session's chat channel. Requires a live connection. */
  dispatchTurn(text: string, attachments?: MessageAttachment[], model?: ModelSelection): void {
    const turnId = createClientTurnId()
    const startedAt = new Date().toISOString()
    const action: StateAction = {
      type: ACTION_TURN_STARTED,
      turnId,
      startedAt,
      message: {
        text,
        origin: {kind: MESSAGE_KIND_USER},
        ...(attachments && attachments.length > 0 ? {attachments} : {}),
        ...(model ? {model} : {}),
      },
    }
    // Track optimistically so a Stop click lands before the daemon echoes the
    // `chat/turnStarted` action back through the subscription. `trackActiveTurn`
    // re-sets the same id on the echo, and terminal actions clear it.
    this.activeTurnId = turnId
    this.dispatchOnChat(action)
  }

  /** Queue a future user message on the observed session's chat channel. Requires a live connection. */
  queueMessage(text: string, attachments?: MessageAttachment[], model?: ModelSelection): string {
    const id = crypto.randomUUID()
    const action: StateAction = {
      type: ACTION_PENDING_MESSAGE_SET,
      kind: PENDING_MESSAGE_KIND_QUEUED,
      id,
      message: {
        text,
        origin: {kind: MESSAGE_KIND_USER},
        ...(attachments && attachments.length > 0 ? {attachments} : {}),
        ...(model ? {model} : {}),
      },
    }
    this.dispatchOnChat(action)
    return id
  }

  /** Remove a queued user message from the observed session's chat channel. Requires a live connection. */
  removeQueuedMessage(id: string): void {
    this.dispatchOnChat({
      type: ACTION_PENDING_MESSAGE_REMOVED,
      kind: PENDING_MESSAGE_KIND_QUEUED,
      id,
    })
  }

  /**
   * Cancel the active turn (or the given `turnId`) by dispatching
   * `chat/turnCancelled`. Returns `false` when there is nothing to cancel or the
   * relay is not currently connected (a Stop click during reconnect is a no-op).
   */
  cancelTurn(turnId?: string): boolean {
    if (this.passive) return false
    const client = this.client
    const chat = this.chatChannel
    const target = turnId ?? this.activeTurnId
    if (!client || !chat || !target) return false
    const action: StateAction = {
      type: ACTION_TURN_CANCELLED,
      turnId: target,
      // This client may have attached after the producer started the turn, so it
      // cannot reconstruct producer-measured duration. Zero is an explicit
      // "unknown from this client" value rather than a cross-clock subtraction.
      duration: 0,
    }
    client.dispatch(chat, action)
    return true
  }

  /**
   * Force the runtime to drop its current connection and re-establish, which
   * causes the next `subscribe` to return a fresh snapshot. Useful when a new
   * consumer mounts on top of an already-active runtime whose cached snapshot
   * may not reflect the current daemon state (e.g. responses streamed in
   * after the original consumer was torn down).
   *
   * A refresh tears down the live client and re-subscribes to pull a fresh
   * snapshot. When an establish is already in flight (connecting/reconnecting),
   * that attempt will itself deliver a fresh snapshot when it lands, so this is a
   * no-op — which collapses a burst of consumers mounting on the same task in one
   * commit into a single connection.
   */
  async refresh(): Promise<void> {
    if (this.disposed || this.establishInFlight) return
    const gen = ++this.connectGen
    this.establishInFlight = true
    await this.teardownClient()
    if (gen !== this.connectGen || this.disposed) {
      this.settleEstablish(gen)
      return
    }
    const signal = this.beginConnectAttempt()
    this.setStatus('connecting')
    try {
      await this.establish(gen, true, signal)
    } catch (err) {
      if (gen !== this.connectGen || this.disposed) return
      if (await this.handleTerminalEstablishError(err)) return
      this.clientEverConnected = false
      const msg = formatError(err)
      this.logger.error(`relay refresh failed: ${msg}`)
      this.setStatus('error', msg)
      this.scheduleReconnect(gen)
    } finally {
      this.settleEstablish(gen)
      this.finishConnectAttempt(signal)
    }
  }

  /** Approve or deny a pending tool call on the observed session's chat channel. Requires a live connection. */
  confirmToolCall({
    approved,
    toolCallId,
    turnId,
    resultConfirmation,
    selectedOptionId,
  }: {
    approved: boolean
    toolCallId: string
    turnId: string
    resultConfirmation?: boolean
    selectedOptionId?: string
  }): void {
    if (this.passive) throw new Error('relay connection is read-only')
    const client = this.client
    if (!client) throw new Error('relay is not connected')
    const chat = this.chatChannel
    if (!chat) throw new Error('relay chat channel is not resolved')

    const action = resultConfirmation
      ? {
          type: 'chat/toolCallResultConfirmed',
          turnId,
          toolCallId,
          approved,
        }
      : approved
        ? {
            type: 'chat/toolCallConfirmed',
            turnId,
            toolCallId,
            approved: true,
            confirmed: 'user-action',
            ...(selectedOptionId ? {selectedOptionId} : {}),
          }
        : {
            type: 'chat/toolCallConfirmed',
            turnId,
            toolCallId,
            approved: false,
            reason: 'denied',
            reasonMessage: 'Denied by user',
            ...(selectedOptionId ? {selectedOptionId} : {}),
          }
    client.dispatch(chat, action as StateAction)
  }

  /**
   * Accept, decline, or cancel a pending input request (`ask_user`/elicitation)
   * on the observed session's chat channel. Requires a live connection.
   */
  completeInput(params: BuildInputCompletedActionParams): void {
    if (this.passive) throw new Error('relay connection is read-only')
    const client = this.client
    if (!client) throw new Error('relay is not connected')
    const chat = this.chatChannel
    if (!chat) throw new Error('relay chat channel is not resolved')
    client.dispatch(chat, buildInputCompletedAction(params))
  }

  /**
   * Register a client tool dynamically. If a tool with the same name already
   * exists it is replaced (upsert). Automatically announces the updated tool
   * set to the host via `session/activeClientSet` if connected.
   */
  registerTool(tool: ClientTool): void {
    const idx = this.tools.findIndex(t => t.definition.name === tool.definition.name)
    if (idx >= 0) {
      this.tools[idx] = tool
    } else {
      this.tools.push(tool)
    }
    if (this.client) this.advertiseActiveClient(this.client)
  }

  /**
   * Unregister a client tool by name. Automatically announces the updated
   * tool set to the host via `session/activeClientSet` if connected.
   *
   * TODO: removing the final tool does not publish an empty `tools: []` to the
   * host (advertiseActiveClient no-ops when buildActiveClient returns undefined).
   * This is acceptable today because existing client tools are never removed at
   * runtime.
   */
  unregisterTool(name: string): void {
    const idx = this.tools.findIndex(t => t.definition.name === name)
    if (idx >= 0) {
      this.tools.splice(idx, 1)
      if (this.client) this.advertiseActiveClient(this.client)
    }
  }

  async dispose(): Promise<void> {
    if (this.disposed) return
    this.disposed = true
    this.connectGen++
    this.abortConnectAttempt()
    await this.teardown()
    this.setStatus('closed')
  }

  private async establish(gen: number, isReconnect: boolean, signal: AbortSignal): Promise<void> {
    // Only reuse the cheap `/reconnect` path (which does NOT re-publish the
    // daemon spawn) when this client id already reached a healthy connection.
    // Otherwise mint a fresh client via `/connect` so a new spawn is published.
    const reconnectClientId = isReconnect && this.clientEverConnected ? this.mcClientId : undefined
    const mc =
      reconnectClientId !== undefined
        ? await (this.options.mcReconnect?.(reconnectClientId, {signal}) ??
            this.mcReconnectDefault(reconnectClientId, signal))
        : await (this.options.mcConnect?.({signal}) ?? this.mcConnectDefault(signal))
    if (gen !== this.connectGen || this.disposed) return
    if (reconnectClientId !== undefined && mc.client_id !== reconnectClientId) {
      throw new Error(`Mission Control reconnect changed client_id from '${reconnectClientId}' to '${mc.client_id}'`)
    }
    this.mcClientId = mc.client_id

    const transport = await this.openTransport(mc)
    if (gen !== this.connectGen || this.disposed) {
      await safeClose(transport)
      return
    }
    this.transport = transport

    const client = this.ahpClientFactory(transport)
    this.client = client
    client.connect()

    const reconnectChannels =
      reconnectClientId !== undefined
        ? [ROOT_CHANNEL, this.observedChannel(), ...(this.chatChannel ? [this.chatChannel] : [])]
        : [ROOT_CHANNEL]
    const localSubscriptions = new Map<URI, Subscription>(
      reconnectChannels.map(channel => [channel, client.attachSubscription(channel)]),
    )
    const handshake =
      reconnectClientId !== undefined
        ? ((await client.reconnect({
            clientId: mc.client_id,
            lastSeenServerSeq: this.lastSeenServerSeq,
            subscriptions: reconnectChannels,
          })) as ReconnectResult & {_meta?: Record<string, unknown>})
        : await client.initialize({
            clientId: mc.client_id,
            protocolVersions: [...CLIENT_SUPPORTED_AHP_VERSIONS],
            initialSubscriptions: [ROOT_CHANNEL],
          })
    if (gen !== this.connectGen || this.disposed) {
      await closeSubscriptions(localSubscriptions.values())
      return
    }
    this.setPassive(isPassiveHandshakeResult(handshake))
    const handshakeSnapshots = 'snapshots' in handshake ? handshake.snapshots : undefined
    const reconnectMissing =
      'missing' in handshake
        ? new Set(handshake.missing)
        : reconnectClientId !== undefined && handshakeSnapshots
          ? new Set(
              reconnectChannels.filter(channel => !handshakeSnapshots.some(snapshot => snapshot.resource === channel)),
            )
          : undefined
    if (reconnectMissing?.has(ROOT_CHANNEL)) {
      await closeSubscriptions(localSubscriptions.values())
      throw new Error('AHP reconnect could not resume the root subscription')
    }
    if ('serverSeq' in handshake) {
      this.lastSeenServerSeq = Math.max(this.lastSeenServerSeq, handshake.serverSeq ?? 0)
    } else if ('actions' in handshake) {
      for (const envelope of handshake.actions) {
        this.applyReceivedAction(envelope, gen)
      }
    } else {
      for (const snapshot of handshakeSnapshots ?? []) {
        this.lastSeenServerSeq = Math.max(this.lastSeenServerSeq, snapshot.fromSeq ?? 0)
      }
    }
    const rootSnapshot = handshakeSnapshots?.find(snapshot => snapshot.resource === ROOT_CHANNEL)
    const isReplayReconnect = 'actions' in handshake
    if (rootSnapshot) {
      this.rootState = rootSnapshot.state as RootState
    } else if (!isReplayReconnect) {
      this.rootState = undefined
    }
    const authenticationSnapshots =
      isReplayReconnect && this.rootState ? [{resource: ROOT_CHANNEL, state: this.rootState}] : handshakeSnapshots
    const authentication = createAwpsAuthenticationContext(
      handshake as typeof handshake & {_meta?: Record<string, unknown>},
      authenticationSnapshots,
      [mc.host_encryption_key],
    )
    // A supplied provider (e.g. chat's sealed data-token) wins for resources it
    // can satisfy; otherwise fall back to MC's resealed identity bearer.
    const optionProvider = this.options.resourceTokenProvider
    // The identity bearer is only offered to GitHub hosts (the client decides
    // GitHub-ness, not the daemon). v0.5.3+ routes the `authenticate` decrypt by
    // resource and rejects an `auth-token` on an unadvertised resource (`-32011`).
    const mcTokenProvider = sealedGitHubTokenProvider(mc.encrypted_github_token, defaultResourceTokenProvider)
    const resourceTokenProvider = optionProvider
      ? withFallbackResourceTokenProvider(optionProvider, mcTokenProvider)
      : mcTokenProvider
    this.resourceTokenProvider = resourceTokenProvider
    this.authentication = authentication
    await this.authenticateAdvertisedResources(client, authenticationSnapshots, resourceTokenProvider, authentication)
    if (gen !== this.connectGen || this.disposed) {
      await closeSubscriptions(localSubscriptions.values())
      return
    }
    const rootSubscription = takeSubscription(localSubscriptions, ROOT_CHANNEL)
    if (!rootSubscription) throw new Error('relay root subscription was not attached')
    void this.consumeRoot(rootSubscription, gen)

    // AHP 0.4.0 split the conversation off the session: turns/tool calls now
    // live on a chat channel. Subscribe to the session only to discover its
    // `defaultChat`, then observe (and dispatch onto) that chat. If the session
    // snapshot does not advertise a default chat yet (e.g. a freshly
    // MC-bootstrapped session), fall back to the deterministic `<session>/chat`
    // form rather than failing the whole connection.
    // Create the daemon session then subscribe; a managed-sandbox observe instead
    // attaches first and creates only on `-32008` (see attachOrCreateSession).
    // Passive connections are read-only, so attachOrCreateSession observes only
    // and never sends createSession.
    const resumedSessionSnapshot = handshakeSnapshots?.find(snapshot => snapshot.resource === this.observedChannel())
    const resumedSessionSubscription = takeSubscription(localSubscriptions, this.observedChannel())
    if (resumedSessionSubscription && reconnectMissing?.has(this.observedChannel())) {
      await resumedSessionSubscription.close()
    }
    const {result: sessionResult, subscription: sessionSubscription} =
      resumedSessionSubscription && !reconnectMissing?.has(this.observedChannel())
        ? {result: {snapshot: resumedSessionSnapshot}, subscription: resumedSessionSubscription}
        : await this.attachOrCreateSession(client, resourceTokenProvider, authenticationSnapshots, authentication)
    if (gen !== this.connectGen || this.disposed) {
      await sessionSubscription.close()
      await closeSubscriptions(localSubscriptions.values())
      return
    }
    if (sessionResult.snapshot) {
      this.lastSeenServerSeq = Math.max(this.lastSeenServerSeq, sessionResult.snapshot.fromSeq ?? 0)
      if (isSessionState(sessionResult.snapshot.state)) {
        this.sessionState = sessionResult.snapshot.state
        await this.authenticatePendingTools(client, gen)
      }
    }
    const advertised = defaultChatChannel(sessionResult.snapshot?.state)
    const chat = advertised ?? chatChannelFor(this.observedChannel())
    if (!advertised) {
      this.logger.info(`relay: session advertised no defaultChat; using derived chat channel ${chat}`)
    }
    this.chatChannel = chat
    // Re-assert the active-client role (and our contributed tools) on every
    // establish while we still hold the session subscription. `createSession`
    // registers the tools at create time, but the host releases the
    // *active-client role* when a connection is finalized without a
    // same-`clientId` reconnect (e.g. a full `/connect` that mints a new
    // `clientId`, or a grace-window expiry). Without this re-claim the tool
    // stays callable but the host has no client to route the call to, so it
    // stalls until the host times it out. Best-effort: a rejected claim must not
    // fail the observe connection.
    this.advertiseActiveClient(client)
    // Keep the session subscription open as a provisional copilotd
    // workaround. The host tracks "last AHP subscriber" by the session URI and
    // drains pending interactive prompts when that count reaches zero, even when
    // the client remains subscribed to the session's default chat URI. Once
    // copilotd treats the chat subscription as keeping the session interactive,
    // this keepalive can go back to an unsubscribe.
    void this.consumeSessionKeepalive(sessionSubscription, gen)

    const resumedChatSnapshot = handshakeSnapshots?.find(snapshot => snapshot.resource === chat)
    const resumedChatSubscription = takeSubscription(localSubscriptions, chat)
    await closeSubscriptions(localSubscriptions.values())
    if (resumedChatSubscription && reconnectMissing?.has(chat)) {
      await resumedChatSubscription.close()
    }
    const {result, subscription} =
      resumedChatSubscription && !reconnectMissing?.has(chat)
        ? {result: {snapshot: resumedChatSnapshot}, subscription: resumedChatSubscription}
        : await this.subscribeWithAuth(client, chat, resourceTokenProvider, authentication)
    if (gen !== this.connectGen || this.disposed) {
      await subscription.close()
      return
    }
    if (result.snapshot) {
      this.lastSeenServerSeq = Math.max(this.lastSeenServerSeq, result.snapshot.fromSeq ?? 0)
      if (isChatState(result.snapshot.state)) {
        this.activeTurnId = result.snapshot.state.activeTurn?.id
        this.options.onSnapshot(result.snapshot.state)
      }
    }
    this.reconnectAttempts = 0
    this.reconnectScheduledForGen = undefined
    this.clientEverConnected = true
    this.setStatus('connected')
    void this.consume(subscription, gen)
  }

  private async createDaemonSession(
    client: AhpClient,
    provider: ResourceTokenProvider,
    snapshots: Array<{resource: URI; state: unknown}> | undefined,
    authentication: AwpsAuthenticationContext,
  ): Promise<void> {
    const opts = this.options.createSession
    if (!opts) return

    let workingDirectory = opts.workingDirectory
    let config: Record<string, unknown> | undefined

    if (opts.project) {
      const resolved = await this.provisionProject(
        client,
        provider,
        authentication,
        this.rootStateFrom(snapshots),
        opts.project,
      )
      workingDirectory = resolved.path
      const baseBranch = opts.project.baseBranch ?? resolved.defaultBranch
      // Without a base branch we must not fall back to the project's shared main
      // checkout (a mutable, shared working dir) — fail provisioning instead.
      if (!baseBranch) {
        throw new ProjectProvisioningError(
          'clone-failed',
          'Could not determine a base branch for the repository worktree.',
        )
      }
      config = {target: SESSION_CONFIG_TARGET_WORKTREE, baseBranch}
    }

    const activeClient = this.buildActiveClient()
    const params: CreateSessionParams = {
      channel: sessionChannel(this.options.sessionId),
      ...(workingDirectory ? {workingDirectory: toWorkingDirectoryUri(workingDirectory)} : {}),
      ...(config ? {config} : {}),
      ...(opts.model ? {model: opts.model} : {}),
      ...(activeClient ? {activeClient} : {}),
    }
    await createSessionWithAuth(client, params, provider, authentication)
  }

  private rootStateFrom(snapshots: Array<{resource: URI; state: unknown}> | undefined): RootState | undefined {
    const rootSnapshot = snapshots?.find(snapshot => snapshot.resource === ROOT_CHANNEL)
    return isRootState(rootSnapshot?.state) ? rootSnapshot.state : undefined
  }

  /**
   * Resolve a repository to a launchable working directory (catalogue match or
   * clone), surfacing progress via `onProvisioning`. Throws {@link ProjectProvisioningError}.
   */
  private provisionProject(
    client: AhpClient,
    provider: ResourceTokenProvider,
    authentication: AwpsAuthenticationContext,
    rootState: RootState | undefined,
    project: RelayProjectProvisioning,
  ): Promise<ResolvedProject> {
    return resolveProjectWorkingDirectory({
      client,
      rootState,
      cloneUrl: project.cloneUrl,
      // ROOT_CHANNEL is already subscribed, so attach a local consumer for clone-progress deltas.
      watchRoot: () => client.attachSubscription(ROOT_CHANNEL),
      authenticate: async () => {
        // Prefer the daemon-advertised resources (host-correct), else the default
        // GitHub API resource; `provider` gates which actually receive a token.
        // Best-effort: public clones proceed without one.
        const advertised = advertisedProtectedResourceUris(rootState)
        const resources = advertised.length > 0 ? advertised : [GITHUB_API_RESOURCE]
        await satisfyResources(
          client,
          resources.map(resource => ({resource, required: true})),
          provider,
          authentication,
        )
      },
      onProgress: status => this.options.onProvisioning?.(status),
    })
  }

  private mcConnectDefault(signal: AbortSignal): Promise<MCConnectResponse> {
    if (!this.options.fetcher) throw new Error('relay connection runtime requires a fetcher or mcConnect')
    return connectToEnvironment(this.options.environmentId, this.options.sessionId, this.options.fetcher, {signal})
  }

  private mcReconnectDefault(clientId: string, signal: AbortSignal): Promise<MCConnectResponse> {
    if (!this.options.fetcher) throw new Error('relay connection runtime requires a fetcher or mcReconnect')
    return reconnectToEnvironment(this.options.environmentId, clientId, this.options.fetcher, {signal})
  }

  private async authenticateAdvertisedResources(
    client: AhpClient,
    snapshots: Array<{resource: URI; state: unknown}> | undefined,
    provider: ResourceTokenProvider,
    authentication: AwpsAuthenticationContext,
  ): Promise<void> {
    const rootSnapshot = snapshots?.find(snapshot => snapshot.resource === ROOT_CHANNEL)
    if (!isRootState(rootSnapshot?.state)) return

    const resources = rootSnapshot.state.agents.flatMap(agent => agent.protectedResources ?? [])
    await satisfyResources(client, resources, provider, authentication)
  }

  /**
   * Establish the session subscription. Default: create the daemon session then
   * subscribe (the create page knows the session is new). When
   * `createSession.attachOrCreate` is set, subscribe first and create only on a
   * genuine `-32008`, so a hard-navigated observe attaches to an existing
   * session instead of re-creating it (which the daemon rejects).
   */
  private async attachOrCreateSession(
    client: AhpClient,
    provider: ResourceTokenProvider,
    snapshots: Array<{resource: URI; state: unknown}> | undefined,
    authentication: AwpsAuthenticationContext,
  ): Promise<Awaited<ReturnType<AhpClient['subscribe']>>> {
    const opts = this.options.createSession
    if (!opts || this.sessionCreated || this.passive) {
      return this.subscribeWithAuth(client, this.observedChannel(), provider, authentication)
    }
    if (!opts.attachOrCreate) {
      await this.createDaemonSession(client, provider, snapshots, authentication)
      this.sessionCreated = true
      return this.subscribeWithAuth(client, this.observedChannel(), provider, authentication)
    }
    try {
      const attached = await this.subscribeWithAuth(client, this.observedChannel(), provider, authentication)
      this.sessionCreated = true
      return attached
    } catch (err) {
      if (!(err instanceof RpcError) || err.code !== SESSION_NOT_FOUND_CODE) throw err
    }
    await this.createDaemonSession(client, provider, snapshots, authentication)
    this.sessionCreated = true
    return this.subscribeWithAuth(client, this.observedChannel(), provider, authentication)
  }

  private async subscribeWithAuth(
    client: AhpClient,
    channel: URI,
    provider: ResourceTokenProvider,
    authentication: AwpsAuthenticationContext,
  ): ReturnType<AhpClient['subscribe']> {
    let attempt = 0
    for (;;) {
      try {
        return await client.subscribe(channel)
      } catch (err) {
        if (!(err instanceof RpcError) || err.code !== AUTH_REQUIRED_CODE || attempt >= MAX_AUTH_RETRIES) {
          throw err
        }
        const satisfied = await satisfyResources(client, authRequiredResources(err.data), provider, authentication)
        if (!satisfied) throw err
        attempt++
      }
    }
  }

  /**
   * Combined active-client publication covering both the caller-supplied
   * customization (`createSession.activeClient`, e.g. `displayName`) and the
   * current tool definitions from {@link tools}.
   *
   * Each `session/activeClientSet` on the session channel is a full replacement
   * rather than a merge — publishing only one source would strand the other,
   * leaving tool calls the agent may still emit with no fulfilment path. The
   * `clientId` MUST match the one passed to `initialize` (`mcClientId`) per the
   * AHP `createSession` contract; it is also what the host stamps on
   * `toolCallStart.contributor` when routing calls back to us.
   */
  private buildActiveClient(): SessionActiveClient | undefined {
    const clientId = this.mcClientId
    if (!clientId) return undefined
    const supplied = this.options.createSession?.activeClient?.(clientId)
    const contributedTools = this.tools.map(tool => tool.definition)
    if (!supplied && contributedTools.length === 0) return undefined
    const suppliedTools = supplied?.tools ?? []
    return {
      ...(supplied ?? {}),
      clientId,
      tools: [...suppliedTools, ...contributedTools],
    }
  }

  /**
   * Re-claim the active-client role for the current `mcClientId` and re-publish
   * the combined active client. Idempotent and best-effort: on a
   * same-`clientId` reconnect this simply re-affirms the role the host already
   * kept; on a full `/connect` (new `clientId`) it hands the active-client role
   * to the new connection so the host routes tool calls here. A rejected claim
   * (e.g. another active client) is logged and never fails the observe
   * connection.
   *
   * No-op when there is nothing to advertise (no tools, no supplied
   * customization) — a toolless observer never publishes.
   */
  private advertiseActiveClient(client: AhpClient): void {
    if (this.passive) return
    const activeClient = this.buildActiveClient()
    if (!activeClient) return
    const action: StateAction = {
      type: ACTION_ACTIVE_CLIENT_SET,
      activeClient,
    }
    try {
      client.dispatch(this.observedChannel(), action)
    } catch (err) {
      this.logger.error(`relay: re-asserting active client failed: ${formatError(err)}`)
    }
  }

  private async openTransport(mc: MCConnectResponse): Promise<AhpTransport> {
    const minted = parseGroupName(mc.groups.broadcast)
    return WpsTransport.connect({
      url: buildWpsUrl(mc),
      toHostGroup: mc.groups.to_host,
      joinGroups: [mc.groups.broadcast, mc.groups.to_client],
      groupValidation: {expected: {uid: minted.uid, eid: minted.eid, cid: mc.client_id}},
      onProtocolError: err => this.logger.error(`relay WPS frame dropped: ${formatError(err)}`),
      ...(this.options.webSocketFactory ? {webSocketFactory: this.options.webSocketFactory} : {}),
    })
  }

  /** Track the active turn id: `turnStarted` sets it, terminal actions clear it. */
  private trackActiveTurn(action: StateAction): void {
    if (action.type === ACTION_TURN_STARTED) {
      this.activeTurnId = action.turnId
      // Not cleared on turn end (see field), so a later `_meta` failure still maps here.
      this.lastTurnId = action.turnId
      return
    }

    const endsTurn =
      action.type === ACTION_TURN_CANCELLED || action.type === ACTION_TURN_COMPLETE || action.type === ACTION_TURN_ERROR
    if (endsTurn && 'turnId' in action && action.turnId === this.activeTurnId) {
      this.activeTurnId = undefined
    }
  }

  private async authenticatePendingTools(client: AhpClient, gen: number): Promise<void> {
    const provider = this.resourceTokenProvider
    const authentication = this.authentication
    if (!provider || !authentication) return

    const requests =
      this.sessionState?.inputNeeded?.filter(
        (request): request is SessionToolAuthenticationRequest =>
          request.kind === SESSION_INPUT_KIND_TOOL_AUTHENTICATION,
      ) ?? []
    for (const request of requests) {
      const inFlightKey = `${gen}:${request.id}`
      if (this.toolAuthenticationInFlight.has(inFlightKey)) continue
      this.toolAuthenticationInFlight.add(inFlightKey)
      try {
        const {resource, requiredScopes} = request.toolCall.auth
        await satisfyResources(
          client,
          [{...resource, required: true, scopes_supported: requiredScopes ?? resource.scopes_supported}],
          provider,
          authentication,
        )
      } finally {
        this.toolAuthenticationInFlight.delete(inFlightKey)
      }
    }
  }

  private async authenticatePendingToolsSafely(client: AhpClient, gen: number): Promise<void> {
    try {
      await this.authenticatePendingTools(client, gen)
    } catch (err) {
      this.logger.error(`relay tool authentication failed: ${formatError(err)}`)
    }
  }

  /**
   * Surface a model-call failure copilotd reports only on the session `_meta`
   * side-channel (not as a `chat/error`), attributed to {@link lastTurnId}. The
   * host never clears it and re-emits `_meta` on any change, so dedupe by
   * `apiCallId` to surface each failure once.
   */
  private handleSessionMetaFailure(action: StateAction): void {
    if (action.type !== ACTION_SESSION_META_CHANGED) return
    const meta = (action as {_meta?: Record<string, unknown> | undefined})._meta ?? undefined
    const raw = meta?.[META_KEY_MODEL_CALL_FAILURE]
    if (!raw || typeof raw !== 'object') return
    const failure = normalizeModelCallFailure(meta)
    if (!failure || !this.lastTurnId) return
    const rawFailure = raw as Record<string, unknown>
    const id =
      nonEmptyString(rawFailure['apiCallId']) ??
      nonEmptyString(rawFailure['serviceRequestId']) ??
      JSON.stringify(failure)
    if (this.seenModelFailureIds.has(id)) return
    this.seenModelFailureIds.add(id)
    this.options.onModelCallFailure?.(failure, this.lastTurnId)
  }

  private applyReceivedAction(envelope: ActionEnvelope, gen: number, sourceChannel = envelope.channel): void {
    if (sourceChannel === ROOT_CHANNEL) {
      if (this.rootState) this.rootState = rootReducer(this.rootState, envelope.action as RootAction)
    } else {
      if (sourceChannel === this.observedChannel()) {
        if (this.sessionState) {
          this.sessionState = sessionReducer(this.sessionState, envelope.action as SessionAction)
          const client = this.client
          if (client) {
            void this.authenticatePendingToolsSafely(client, gen)
          }
        }
        this.handleSessionMetaFailure(envelope.action)
      } else {
        this.trackActiveTurn(envelope.action)
      }
      try {
        this.options.onAction(envelope)
      } catch (err) {
        this.logger.error(`relay action listener failed: ${formatError(err)}`)
      }
      if (sourceChannel !== this.observedChannel()) {
        this.handleClientToolAction(envelope, gen)
      }
    }
    this.lastSeenServerSeq = Math.max(this.lastSeenServerSeq, envelope.serverSeq ?? 0)
  }

  private async consume(subscription: Subscription, gen: number): Promise<void> {
    try {
      for await (const evt of subscription) {
        if (this.disposed || gen !== this.connectGen) return
        if (evt.type !== 'action') continue
        this.applyReceivedAction(evt.params, gen, this.chatChannel)
      }
    } catch (err) {
      if (gen === this.connectGen && !this.disposed) {
        this.logger.error(`relay subscription ended: ${formatError(err)}`)
      }
    }
    if (gen === this.connectGen && !this.disposed) {
      this.scheduleReconnect(gen)
    }
  }

  private async consumeSessionKeepalive(subscription: Subscription, gen: number): Promise<void> {
    try {
      for await (const evt of subscription) {
        if (this.disposed || gen !== this.connectGen) return
        if (evt.type === 'action') {
          this.applyReceivedAction(evt.params, gen, this.observedChannel())
        }
      }
    } catch (err) {
      if (gen === this.connectGen && !this.disposed) {
        this.logger.error(`relay session subscription ended: ${formatError(err)}`)
      }
    }

    // Session-scoped actions (like `session/titleChanged` from client tool
    // completions) ride this stream, so a silent exit would leave those
    // actions undeliverable while the chat subscription may still look healthy.
    // Treat termination as a reconnect condition; the dedup in
    // `scheduleReconnect` collapses this into the chat stream's schedule when
    // both drop together.
    if (gen === this.connectGen && !this.disposed) {
      this.scheduleReconnect(gen)
    }
  }

  private async consumeRoot(subscription: Subscription, gen: number): Promise<void> {
    try {
      for await (const evt of subscription) {
        if (this.disposed || gen !== this.connectGen) return
        if (evt.type === 'action') this.applyReceivedAction(evt.params, gen, ROOT_CHANNEL)
      }
    } catch (err) {
      if (gen === this.connectGen && !this.disposed) {
        this.logger.error(`relay root subscription ended: ${formatError(err)}`)
      }
    }
    if (gen === this.connectGen && !this.disposed) {
      this.scheduleReconnect(gen)
    }
  }

  private scheduleReconnect(gen: number): void {
    if (this.disposed || gen !== this.connectGen || this.terminated) return
    if (this.reconnectScheduledForGen === gen) return
    this.reconnectScheduledForGen = gen
    const attempt = this.reconnectAttempts++
    const delay = Math.min(RECONNECT_BASE_DELAY_MS * 2 ** attempt, RECONNECT_MAX_DELAY_MS)
    this.setStatus('reconnecting')
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined
      if (this.disposed || gen !== this.connectGen) return
      const reconnectGen = ++this.connectGen
      void this.reestablish(reconnectGen)
    }, delay)
  }

  private async reestablish(gen: number): Promise<void> {
    this.establishInFlight = true
    await this.teardownClient()
    if (gen !== this.connectGen || this.disposed) {
      this.settleEstablish(gen)
      return
    }
    const signal = this.beginConnectAttempt()
    try {
      await this.establish(gen, true, signal)
    } catch (err) {
      if (gen !== this.connectGen || this.disposed) return
      if (await this.handleTerminalEstablishError(err)) return
      // Failed to (re)establish; escalate the next attempt to a full `/connect`
      // (new client id + spawn) rather than reconnecting the same dead client.
      this.clientEverConnected = false
      // A mid-session reconnect failure is transient and self-healing (the next
      // backoff attempt recovers), so log it at info — not error. This keeps a
      // relay that is reconnecting in its dispose-grace window from tripping
      // `vitest-fail-on-console` (which fails only on console.error/warn) in an
      // unrelated test. A first-establish failure stays error-level in connect().
      this.logger.info(`relay reconnect failed: ${formatError(err)}`)
      this.scheduleReconnect(gen)
    } finally {
      this.settleEstablish(gen)
      this.finishConnectAttempt(signal)
    }
  }

  private beginConnectAttempt(): AbortSignal {
    this.abortConnectAttempt()
    const controller = new AbortController()
    this.connectAbortController = controller
    return controller.signal
  }

  private finishConnectAttempt(signal: AbortSignal): void {
    if (this.connectAbortController?.signal === signal) {
      this.connectAbortController = undefined
    }
  }

  private abortConnectAttempt(): void {
    if (this.reconnectTimer !== undefined) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = undefined
    }
    this.connectAbortController?.abort()
    this.connectAbortController = undefined
  }

  private async teardown(): Promise<void> {
    await this.teardownClient()
  }

  private async teardownClient(): Promise<void> {
    const client = this.client
    this.client = null
    this.ownedToolCalls.clear()
    this.pendingClientToolReady.clear()
    if (client) {
      try {
        await client.shutdown()
      } catch {
        // best-effort
      }
    }
    const transport = this.transport
    this.transport = null
    if (transport) await safeClose(transport)
  }

  private setStatus(status: RelayStatus, error?: string): void {
    this.status = status
    this.options.onStatus?.(status, error)
  }

  private setPassive(passive: boolean): void {
    if (this.passiveKnown && passive === this.passive) return
    this.passiveKnown = true
    this.passive = passive
    this.options.onPassiveChange?.(passive)
  }

  /**
   * Drive the fulfilment half of a contributed client tool. The host attributes
   * a call to us on `toolCallStart` (via a client `contributor` carrying our
   * `clientId`) and streams the arguments; once it reaches `toolCallReady`
   * (auto-confirmed to `running` for client tools) we run the tool and report
   * the result with `chat/toolCallComplete`. We take ownership of every call
   * routed to our `clientId` — even for a tool we did not register — so a
   * mis-routed call is always completed (with a failure result) rather than
   * stalling the turn.
   */
  private handleClientToolAction(env: ActionEnvelope, gen: number): void {
    if (this.passive) return
    // Continue processing even when tools is empty: calls already owned (routed
    // before the last tool was unregistered) must still be completed so the
    // turn does not stall.
    if (this.tools.length === 0 && this.ownedToolCalls.size === 0) return
    const action = env.action as {
      type?: string
      toolCallId?: string
      turnId?: string
      toolName?: string
      toolInput?: string
      content?: string
      confirmed?: string
      contributor?: {kind?: string; clientId?: string}
    }
    if (!action.toolCallId) return

    if (action.type === ACTION_TOOL_CALL_START) {
      const owned = action.contributor?.kind === 'client' && action.contributor.clientId === this.mcClientId
      if (!owned) {
        // Routed to a different client: drop any ready we buffered for it so the
        // out-of-order buffer never retains calls that were never ours.
        this.pendingClientToolReady.delete(action.toolCallId)
        return
      }
      if (action.turnId && action.toolName) {
        const call: OwnedToolCall = {
          toolCallId: action.toolCallId,
          turnId: action.turnId,
          toolName: action.toolName,
          deltas: '',
        }
        this.ownedToolCalls.set(action.toolCallId, call)
        // A `toolCallReady` may have arrived before this start; now that the
        // call's turn/tool identity is known, replay it and complete the call.
        const pendingReady = this.pendingClientToolReady.get(action.toolCallId)
        if (pendingReady) {
          this.pendingClientToolReady.delete(action.toolCallId)
          this.processOwnedReady(call, pendingReady.toolInput, pendingReady.needsConfirmation, gen)
        }
      }
      return
    }

    if (action.type === ACTION_TOOL_CALL_DELTA) {
      const call = this.ownedToolCalls.get(action.toolCallId)
      if (call && action.content) call.deltas += action.content
      return
    }

    if (action.type === ACTION_TOOL_CALL_READY) {
      // The host leaves a client tool in `pending-confirmation` unless it
      // auto-confirms it (`confirmed` set, typically `'not-needed'`). When it
      // does not, we must approve the call ourselves — otherwise the host's
      // permission gate never opens and the turn stalls until the client-tool
      // timeout fires. The host then re-emits `toolCallReady` (now `running`,
      // carrying the resolved `toolInput`), which is where we complete the call.
      const needsConfirmation = action.confirmed == null
      const call = this.ownedToolCalls.get(action.toolCallId)
      if (!call) {
        // Ready before start: buffer the input (ownership is unknown until an
        // owned start arrives) and act then, rather than dropping the event and
        // stalling the turn until the host times the call out.
        this.pendingClientToolReady.set(action.toolCallId, {
          ...(action.toolInput ? {toolInput: action.toolInput} : {}),
          needsConfirmation,
        })
        return
      }
      this.processOwnedReady(call, action.toolInput, needsConfirmation, gen)
    }
  }

  /**
   * Act on a `toolCallReady` for a call we own. A call the host left in
   * `pending-confirmation` is approved and kept — we complete it only on the
   * follow-up `running` ready that carries the resolved `toolInput`. An
   * already-confirmed (or auto-confirmed) call is completed straight away.
   */
  private processOwnedReady(
    call: OwnedToolCall,
    toolInput: string | undefined,
    needsConfirmation: boolean,
    gen: number,
  ): void {
    if (needsConfirmation && !call.confirmed) {
      call.confirmed = true
      if (toolInput) call.readyInput = toolInput
      this.confirmOwnedToolCall(call, gen)
      return
    }
    this.ownedToolCalls.delete(call.toolCallId)
    this.runOwnedToolCall(call, toolInput ?? call.readyInput, gen)
  }

  /**
   * Fulfil an owned tool call and report its `chat/toolCallComplete`, preferring
   * the ready event's `toolInput` and falling back to the streamed deltas. By
   * this point any required confirmation has already been sent (see
   * {@link processOwnedReady}), so the call has reached `running`.
   */
  private runOwnedToolCall(call: OwnedToolCall, toolInput: string | undefined, gen: number): void {
    const resolvedInput = toolInput ?? (call.deltas || undefined)
    const withInput: ClientToolCall = {
      toolCallId: call.toolCallId,
      turnId: call.turnId,
      toolName: call.toolName,
      ...(resolvedInput ? {toolInput: resolvedInput} : {}),
    }
    const tool = this.tools.find(entry => entry.definition.name === call.toolName)
    void this.completeClientTool(tool, withInput, gen)
  }

  /**
   * Approve an owned client tool call the host left in `pending-confirmation`.
   * Client tools carry no user-facing permission UI here, so the runtime
   * auto-approves them (mirroring `confirmToolCall`'s approve branch) to open
   * the host's permission gate before dispatching the result.
   */
  private confirmOwnedToolCall(call: OwnedToolCall, gen: number): void {
    if (this.disposed || gen !== this.connectGen) return
    const client = this.client
    const chat = this.chatChannel
    if (!client || !chat) return
    const action: StateAction = {
      type: ACTION_TOOL_CALL_CONFIRMED,
      turnId: call.turnId,
      toolCallId: call.toolCallId,
      approved: true,
      confirmed: 'user-action',
    } as StateAction
    try {
      client.dispatch(chat, action)
    } catch (err) {
      this.logger.error(`relay: confirming client tool call failed: ${formatError(err)}`)
    }
  }

  private async completeClientTool(tool: ClientTool | undefined, call: ClientToolCall, gen: number): Promise<void> {
    let result: ToolCallResult
    if (!tool) {
      // The host routed a call to us for a tool we never registered. Complete it
      // with a failure so the host can surface the error and advance the turn.
      result = {
        success: false,
        pastTenseMessage: '',
        error: {message: `No client tool registered for "${call.toolName}"`},
      }
    } else {
      try {
        result = (await tool.fulfil?.(call)) ?? {success: true, pastTenseMessage: ''}
      } catch (err) {
        result = {success: false, pastTenseMessage: '', error: {message: formatError(err)}}
      }
    }
    if (this.disposed || gen !== this.connectGen) return
    const client = this.client
    const chat = this.chatChannel
    if (!client || !chat) return
    const action: StateAction = {
      type: ACTION_TOOL_CALL_COMPLETE,
      turnId: call.turnId,
      toolCallId: call.toolCallId,
      result,
    }
    try {
      client.dispatch(chat, action)
    } catch (err) {
      this.logger.error(`relay: completing client tool call failed: ${formatError(err)}`)
    }
  }
}

/**
 * Strip the Web PubSub `access_token` from any string before it is logged or
 * surfaced. Transport errors can embed the full connection URL (which carries
 * `?access_token=…`) in their message, so redact it defensively at the single
 * point where errors become strings.
 */
function redactSecrets(message: string): string {
  return message.replace(/(access_token=)[^&\s"']+/gi, '$1[REDACTED]')
}

function formatError(err: unknown): string {
  return redactSecrets(err instanceof Error ? err.message : String(err))
}

/**
 * Agent-level protected-resource URIs the daemon advertised — the resources to
 * attempt `authenticate` for before a private clone. The token provider gates
 * which actually receive the identity bearer, so non-GitHub ones are skipped.
 */
function advertisedProtectedResourceUris(rootState: RootState | undefined): string[] {
  if (!rootState) return []
  return rootState.agents.flatMap(agent => (agent.protectedResources ?? []).map(meta => meta.resource))
}

function takeSubscription(subscriptions: Map<URI, Subscription>, resource: URI): Subscription | undefined {
  const subscription = subscriptions.get(resource)
  subscriptions.delete(resource)
  return subscription
}

async function closeSubscriptions(subscriptions: Iterable<Subscription>): Promise<void> {
  await Promise.all([...subscriptions].map(async subscription => subscription.close()))
}

async function safeClose(transport: AhpTransport): Promise<void> {
  try {
    await transport.close()
  } catch {
    // best-effort
  }
}
